import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin } from 'rxjs';
import {
    FileTransfersService,
    IOcbEnvironmentAccess,
    IOcbFileTransfer,
    IOcbLocalAgentCommand,
    LocalAgentsService,
    OcbLocalAgentCommandStatus,
    UserService
} from '@app/core/';
import { LocalAgentCommandComponent } from '../../shared';

@Component({
    selector: 'app-home-file-transfers-execution',
    templateUrl: './home-file-transfers-execution.component.html',
    styleUrls: ['./home-file-transfers-execution.component.scss'],
})
export class HomeFileTransfersExecutionComponent implements AfterViewInit, OnDestroy {
    @ViewChild('agentCommandPopup')
    public agentCommandPopup: LocalAgentCommandComponent;

    public environments: IOcbEnvironmentAccess[] = [];
    public fileTransfers: IOcbFileTransfer[];
    public filteredFileTransfers: IOcbFileTransfer[];
    public isLoadPanelVisible: boolean = false;
    public isTransferInProgress: boolean = false;
    public selectedEnvironment: IOcbEnvironmentAccess = {};
    public selectedFileTransfer: IOcbFileTransfer;
    public transferProgression: string = '';
    public transferStep: number = 0;

    private commandCheckTimer;
    private hideTransferProgressionTimer;
    private statusLabels: any;

    private checkTransfer(command: IOcbLocalAgentCommand) {
        this.transferProgression = this.statusLabels[OcbLocalAgentCommandStatus[command.status]];

        if (command.status == OcbLocalAgentCommandStatus.Running) {
            this.transferStep = 2;
            this.commandCheckTimer = setTimeout(() => this.getAndCheckCommand(command.localAgent.id, command.id), 3000);
        }
        else if (command.status == OcbLocalAgentCommandStatus.Done) {
            this.transferStep = 3;
            forkJoin([
                this.translateService.get('fileTransfers.msgTransferCompleted'),
                this.translateService.get('fileTransfers.msgTransferCompletedNoFileTransferred'),
                this.translateService.get('fileTransfers.msgTransferCompletedFileTransferred'),
                this.translateService.get('fileTransfers.msgTransferCompletedFilesTransferred')
            ])
                .subscribe({
                    next: (messages) => {
                        const filesCount = command.fileTransferLogs.length;
                        let message = '';
                        if (filesCount == 0) message = `${messages[0]}, ${messages[1]}`;
                        else if (filesCount == 1) message = `${messages[0]}, ${filesCount} ${messages[2]}`;
                        else message = `${messages[0]}, ${filesCount} ${messages[3]}`;

                        notify(message, 'success', 8000);
                        this.hideTransferProgressionTimer = setTimeout(() => this.hideTransferProgression(), 2000);
                        this.showCommandPopup(command);
                    },
                    error: (error) => this.handleError(error)
                });
        }
        else if (command.status == OcbLocalAgentCommandStatus.Error) {
            this.transferStep = 3;
            this.translateService.get('fileTransfers.msgTransferCompletedWithError').subscribe({
                next: (message) => {
                    notify(message, 'error', 8000);
                    this.hideTransferProgressionTimer = setTimeout(() => this.hideTransferProgression(), 2000);
                    this.showCommandPopup(command);
                },
                error: (error) => this.handleError(error)
            });
        }
        else if (command.status == OcbLocalAgentCommandStatus.Expired) {
            this.transferStep = 3;
            this.translateService.get('fileTransfers.msgTransferExpired').subscribe({
                next: (message) => {
                    notify(message, 'error', 8000);
                    this.hideTransferProgressionTimer = setTimeout(() => this.hideTransferProgression(), 2000);
                    this.showCommandPopup(command);
                },
                error: (error) => this.handleError(error)
            });
        }
        else {
            //status: Waiting
            this.transferStep = 1;
            this.commandCheckTimer = setTimeout(() => this.getAndCheckCommand(command.localAgent.id, command.id), 3000);
        }
    }

    private getAndCheckCommand(agentId, commandId) {
        this.localAgentsService.getCommand(agentId, commandId).subscribe({
            next: (command) => this.checkTransfer(command),
            error: (error) => {
                this.handleError(error);
                this.isTransferInProgress = false;
                this.transferProgression = '';
            }
        });
    }

    private hideTransferProgression() {
        this.isTransferInProgress = false;
        this.transferProgression = '';
    }

    private showCommandPopup(command: IOcbLocalAgentCommand) {
        this.agentCommandPopup.agentCommand = command;
        this.agentCommandPopup.visible = true;
    }

    constructor(private userService: UserService,
        private translateService: TranslateService,
        private fileTransfersService: FileTransfersService,
        private localAgentsService: LocalAgentsService) { }

    public ngAfterViewInit(): void {
        this.userService.user.subscribe(admin => {
            this.environments = admin.environments.filter(e => e.canExecuteTransfers);
            if (this.environments.length > 0) {
                this.selectedEnvironment = this.environments.find(env => env.environmentId === this.userService.currentEnvironment);
            }
        });
        this.fileTransfersService.getFileTransfers().subscribe(fileTransfers => {
            this.fileTransfers = fileTransfers;
            if (this.selectedEnvironment && !this.filteredFileTransfers) {
                this.setFilteredFileTransfers();
            }
        });
        this.translateService.get('localAgentsCommandsStatus').subscribe(labels => {
            this.statusLabels = labels;
        });
    }

    public ngOnDestroy() {
        if (this.commandCheckTimer) clearTimeout(this.commandCheckTimer);
        if (this.hideTransferProgressionTimer) clearTimeout(this.hideTransferProgressionTimer);
    }


    public setFilteredFileTransfers(): void {
        if (!this.fileTransfers) { return; }
        this.filteredFileTransfers = this.fileTransfers.filter(ft => ft.environmentId === this.selectedEnvironment.environmentId);
        this.selectedFileTransfer = null;
    }

    public executeFileTransfer(): void {
        forkJoin([
            this.translateService.get('fileTransfers.msgExecuteConfirm'),
            this.translateService.get('fileTransfers.msgExecuteConfirmTitle')
        ]).subscribe({
            next: (messages) => {
                confirm(messages[0], messages[1]).then((confirmed) => {
                    if (!confirmed) return;
                    this.isLoadPanelVisible = true;
                    this.transferStep = 0;
                    this.fileTransfersService.executeFileTransfer(this.selectedFileTransfer.id, false).subscribe({
                        next: (command) => {
                            this.isTransferInProgress = true;
                            this.checkTransfer(command);
                        },
                        error: (error) => this.handleError(error),
                        complete: () => this.isLoadPanelVisible = false
                    });
                });
            },
            error: (err: any) => this.handleError(err)
        });
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
